/* jshint ignore:start */
import { useContext, useEffect, useState } from "react";
import axios from "axios";
import { MyContext } from "../../Context/MyProvider";
import { showToast } from "../../showTost";

const EMPTY = {
  company_name: "",
  contact_email: "",
  contact_phone: "",
  employee_count: "",
  plan_id: "",
  message: "",
};

export default function Corporate() {
  const { ApiBaseUrl } = useContext(MyContext);
  const [form, setForm] = useState(EMPTY);
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    axios
      .post(`${ApiBaseUrl}/subscription/plans`, {})
      .then((r) => setPlans(r.data.plans || []))
      .catch(() => setPlans([]));
  }, [ApiBaseUrl]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.company_name.trim() || !form.contact_email.trim()) {
      showToast("error", "Please add your company name and a work email.");
      return;
    }
    setBusy(true);
    axios
      .post(`${ApiBaseUrl}/subscription/corporate_enquiry`, form)
      .then((r) => {
        if (r.data.Result) {
          setSent(true);
          setForm(EMPTY);
          showToast("success", r.data.message);
        } else {
          showToast("error", r.data.message || "Could not send that enquiry.");
        }
      })
      .catch(() => showToast("error", "Something went wrong. Please try again."))
      .finally(() => setBusy(false));
  };

  return (
    <div className="width-container py-[40px]">
      <div className="max-w-[760px]">
        <p className="text-[13px] uppercase tracking-wide font-[700] text-rm-primary">Corporate Wellness</p>
        <h1 className="text-[34px] leading-tight font-[800] text-rm-ink mt-[6px]">
          Healthcare your team will actually use
        </h1>
        <p className="text-rm-muted mt-[10px]">
          Teleconsults, annual checkups, on-site screenings and 24×7 emergency access for your staff and
          their families — with a simple HR dashboard to see it all. From ₹299 per employee per month for
          teams of 20 or more.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-[14px] mt-[26px]">
        {[
          ["Teleconsultation", "Two consults per employee every month, any specialty."],
          ["Annual health check", "One full-body checkup per employee, at home or at a partner lab."],
          ["On-site screening", "Quarterly BP and blood-sugar camps at your office."],
          ["24×7 emergency line", "Round-the-clock access to a doctor for urgent situations."],
        ].map(([title, body]) => (
          <div key={title} className="border border-rm-line rounded-[16px] p-[18px] bg-white">
            <h4 className="font-[700] text-rm-ink">{title}</h4>
            <p className="text-[14px] text-rm-muted mt-[6px]">{body}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-[24px] mt-[34px]">
        <form onSubmit={submit} className="border border-rm-line rounded-[20px] p-[24px] bg-white shadow-rm-soft">
          <h2 className="text-[22px] font-[800] text-rm-ink">Request a proposal</h2>
          <p className="text-rm-muted text-[14px] mt-[4px]">
            Tell us about your team and we will come back within one working day.
          </p>

          <div className="grid sm:grid-cols-2 gap-[14px] mt-[18px]">
            <div className="sm:col-span-2">
              <label className="text-[13px] text-rm-muted">Company name *</label>
              <input className="w-full border border-rm-line rounded-[10px] px-[12px] py-[10px] mt-[4px]"
                     value={form.company_name} onChange={set("company_name")} placeholder="Acme Technologies Pvt Ltd" required />
            </div>
            <div>
              <label className="text-[13px] text-rm-muted">Work email *</label>
              <input type="email" className="w-full border border-rm-line rounded-[10px] px-[12px] py-[10px] mt-[4px]"
                     value={form.contact_email} onChange={set("contact_email")} placeholder="hr@company.com" required />
            </div>
            <div>
              <label className="text-[13px] text-rm-muted">Phone</label>
              <input className="w-full border border-rm-line rounded-[10px] px-[12px] py-[10px] mt-[4px]"
                     value={form.contact_phone} onChange={set("contact_phone")} placeholder="+91 " />
            </div>
            <div>
              <label className="text-[13px] text-rm-muted">Number of employees</label>
              <input type="number" min="1" className="w-full border border-rm-line rounded-[10px] px-[12px] py-[10px] mt-[4px]"
                     value={form.employee_count} onChange={set("employee_count")} placeholder="50" />
            </div>
            <div>
              <label className="text-[13px] text-rm-muted">Plan of interest</label>
              <select className="w-full border border-rm-line rounded-[10px] px-[12px] py-[10px] mt-[4px]"
                      value={form.plan_id} onChange={set("plan_id")}>
                <option value="">Not sure yet</option>
                {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-[13px] text-rm-muted">Anything else we should know?</label>
              <textarea rows="3" className="w-full border border-rm-line rounded-[10px] px-[12px] py-[10px] mt-[4px]"
                        value={form.message} onChange={set("message")}
                        placeholder="Offices, shift patterns, what you want covered…" />
            </div>
          </div>

          <button type="submit" disabled={busy}
                  className="w-full mt-[18px] py-[11px] rounded-[12px] font-[700] text-[15px] bg-rm-gradient text-white">
            {busy ? "Sending…" : "Request a proposal"}
          </button>
          {sent && (
            <p className="text-[14px] text-rm-success mt-[12px]">
              Thanks — your enquiry is with our corporate team.
            </p>
          )}
        </form>

        <div className="border border-rm-line rounded-[20px] p-[24px] bg-rm-softbg">
          <h3 className="text-[20px] font-[800] text-rm-ink">How it works</h3>
          <ol className="mt-[14px] space-y-[14px] text-[14px] text-rm-ink">
            {[
              ["Share your team size", "We size the plan and send pricing within a working day."],
              ["We onboard your staff", "Upload a phone list and everyone is activated together."],
              ["Employees just book", "They use the normal RapidMediK app — cover is applied automatically."],
              ["You see the numbers", "Utilisation and spend in one HR dashboard."],
            ].map(([t, b], i) => (
              <li key={t} className="flex gap-[12px]">
                <span className="shrink-0 w-[26px] h-[26px] rounded-full bg-rm-gradient text-white font-[700] text-[13px] flex items-center justify-center">{i + 1}</span>
                <span><strong className="text-rm-ink">{t}.</strong> <span className="text-rm-muted">{b}</span></span>
              </li>
            ))}
          </ol>

          <div className="mt-[20px] pt-[18px] border-t border-rm-line">
            <p className="text-[13px] text-rm-muted">Prefer to talk?</p>
            <a href="tel:+919429694496" className="block font-[700] text-rm-ink text-[17px] mt-[2px]">+91 94296 94496</a>
            <a href="mailto:care@rapidmedik.in" className="block font-[600] text-rm-primary text-[15px]">care@rapidmedik.in</a>
            <p className="text-[13px] text-rm-muted mt-[8px]">Currently serving Hyderabad &amp; Secunderabad.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
